// useTenantBrand — the active tenant's product brand (BigPack/BigFarm/…),
// falling back to the BigAgri platform brand when there's no active tenant
// (pre-auth, or a platform admin with no tenant claim). Sibling to
// useLetterhead / useFranchise. See src/lib/brand.ts.

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useActiveTenant } from './useActiveTenant'
import { useAppearance, isSharedDevice, LIGHT_THEME_COLOR } from './appearance'
import { brandForKind, loaderColorOf, PLATFORM_BRAND, type BrandDef } from './brand'

export function useTenantBrand(): BrandDef {
  const { data: tenant } = useActiveTenant()
  return tenant?.kind ? brandForKind(tenant.kind) : PLATFORM_BRAND
}

// useBrandChrome — applies the active brand to the document chrome: the tab
// title, the OS theme-colour and `--brand-color` on <html> (the colour
// <BrandLoader/> draws in), and remembers that colour for index.html's
// pre-boot loader. Mount once inside the authed app shell.
// Per-page titles can layer on top later via a usePageTitle() helper.
export function useBrandChrome(): void {
  const brand = useTenantBrand()
  const [appearance] = useAppearance()
  const { pathname } = useLocation()
  const light = appearance === 'light' && !isSharedDevice(pathname)
  useEffect(() => {
    document.title = brand.appName
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute('content', light ? LIGHT_THEME_COLOR : brand.themeColor)
    const colour = loaderColorOf(brand)
    document.documentElement.style.setProperty('--brand-color', colour)
    // index.html's pre-boot loader reads this on the next cold start.
    try { localStorage.setItem('bigagri-brand-color', colour) } catch { /* storage blocked — the boot B stays green */ }
  }, [brand, light])
}
